import React, { type JSX } from 'react';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import { useMemo } from 'react';
import useGame from '@resources/js/contexts/useGame';
import TalentTreePercentBadge from '@resources/js/components/TalentTreePercentBadge/TalentTreePercentBadge';
import cx from 'classnames';
import useTableHiddenColumns from '@resources/js/contexts/useTableHiddenColumns';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export interface BuildsTalentRowsSectionViewModel {
  title: string;
  description: string;
  talents: BuildsTalentRowTalentModel[];
  rowLabels: Array<string | number>;
  talentRowLabelBorderImages: Array<TalentRowBorderImage>;
  decorVariables: Record<string, string> | null;
}

interface BuildsTalentRowTalentModel {
  definition: ClassicWarcraftTalentRowDefinition;
  pickRate: number;
  isSelected: boolean;
  fullLabel: string;
  metricValue: string | null;
}

interface ClassicWarcraftTalentRowDefinition {
  spellId: number;
  rowIndex: number;
  columnIndex: number;
  name: string;
  icon: string;
  externalId: number | null;
}

type TalentRowBorderImage = {
  modifier: string | null;
  source: string;
  activeSource: string | null;
  slice: string;
  width: string;
  aspectRatio: string | null;
};

export default function BuildsTalentRowsSection({
  title,
  description,
  talents,
  rowLabels,
  decorVariables,
  talentRowLabelBorderImages,
}: BuildsTalentRowsSectionViewModel): JSX.Element {
  return (
    <VerticalContent
      className={'builds-talent-rows-section'}
      style={decorVariables ?? undefined}
    >
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
      `}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <TalentGrid
        talents={talents}
        rowLabels={rowLabels}
        talentRowLabelBorderImages={talentRowLabelBorderImages}
      />
    </VerticalContent>
  );
}

function TalentRowTalent({
  definition,
  pickRate,
  isSelected,
  fullLabel,
  metricValue,
}: BuildsTalentRowTalentModel): JSX.Element {
  const game = useGame();
  // this looks weird, but the `BuildsAdvancedSettings` sets this value for showing dps/hps.
  const { hiddenColumns } = useTableHiddenColumns();
  const showDpsHps =
    !hiddenColumns.includes('dps') || !hiddenColumns.includes('hps');
  const tooltipUrl = `${game.talentExternalSitePath}${
    definition.externalId ?? definition.spellId
  }`;

  return (
    <div
      role={'cell'}
      aria-label={fullLabel}
      className={cx([
        'builds-talent-rows-section__talent-row-talent',
        isSelected && 'builds-talent-rows-section__talent-row-talent--active',
        showDpsHps &&
          'builds-talent-rows-section__talent-row-talent--show-metric',
      ])}
    >
      <a
        className={cx([
          'builds-talent-rows-section__talent-row-talent__link',
          isSelected &&
            'builds-talent-rows-section__talent-row-talent__link--active',
        ])}
        href={tooltipUrl}
        target='_blank'
        rel='noreferrer'
        onClick={(e) => e.stopPropagation()}
      >
        <div className={'builds-talent-rows-section__talent-row-talent__icon'}>
          <div className='builds-talent-rows-section__talent-row-talent__icon_container'>
            <img
              className={
                'builds-talent-rows-section__talent-row-talent__icon_image'
              }
              src={`${game.assetServerBaseUrl}/img/${game.lowercaseName}/abilities/${definition.icon}`}
              alt={definition.name}
              loading={'lazy'}
            />
            <div
              className={cx([
                'builds-talent-rows-section__talent-row-talent__icon_overlay',
                isSelected &&
                  'builds-talent-rows-section__talent-row-talent__icon_overlay--active',
              ])}
            />
          </div>
          <TalentTreePercentBadge percent={pickRate} />
        </div>
        <span className={'builds-talent-rows-section__talent-row-talent__name'}>
          {definition.name}
        </span>
        {showDpsHps && metricValue && (
          <span
            className={
              'builds-talent-rows-section__talent-row-talent__metric-value'
            }
          >
            <EnhancedMarkdown markdown={metricValue} />
          </span>
        )}
      </a>
    </div>
  );
}

function TalentGrid({
  talents,
  rowLabels,
  talentRowLabelBorderImages,
}: Pick<
  BuildsTalentRowsSectionViewModel,
  'talents' | 'rowLabels' | 'talentRowLabelBorderImages'
>): JSX.Element {
  // group talents into rows, then sort each row and sort the rows themselves.
  const byRow = useMemo(() => {
    const byRow = new Map<number, BuildsTalentRowTalentModel[]>();
    for (const talent of talents) {
      if (!byRow.has(talent.definition.rowIndex)) {
        byRow.set(talent.definition.rowIndex, []);
      }

      byRow.get(talent.definition.rowIndex)?.push(talent);
    }

    for (const row of byRow.values()) {
      row.sort((a, b) => a.definition.columnIndex - b.definition.columnIndex);
    }

    const values = Array.from(byRow.values());
    values.sort((a, b) => a[0].definition.rowIndex - b[0].definition.rowIndex);

    return values;
  }, [talents]);

  return (
    <div
      className={cx([
        'builds-talent-rows-section__talent-grid',
        talentRowLabelBorderImages.length == 0 &&
          'builds-talent-rows-section__talent-grid--border',
      ])}
    >
      {byRow.map((row, index) => (
        <div
          className={'builds-talent-rows-section__talent-grid-row'}
          role={'row'}
          key={index}
          style={{ ['--row-choices']: row.length } as React.CSSProperties}
        >
          <div
            className={
              'builds-talent-rows-section__talent-row-talent builds-talent-rows-section__talent-row-talent-label-image'
            }
            style={
              talentRowLabelBorderImages[index]
                ? {
                    aspectRatio:
                      talentRowLabelBorderImages[index].aspectRatio ||
                      undefined,
                    borderImageSource: `url(${talentRowLabelBorderImages[index].source})`,
                    borderImageSlice: talentRowLabelBorderImages[index].slice,
                    borderImageWidth: talentRowLabelBorderImages[index].width,
                  }
                : undefined
            }
          >
            {talentRowLabelBorderImages[index] ? '' : rowLabels[index]}
          </div>
          <div
            className={
              'builds-talent-rows-section__talent-row-talent builds-talent-rows-section__talent-row-talent-label-text'
            }
          >
            {rowLabels[index]}
          </div>
          {row.map((talent) => (
            <TalentRowTalent {...talent} key={talent.definition.spellId} />
          ))}
        </div>
      ))}
    </div>
  );
}
